import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  exitVideoFullscreen,
  isLandscapeVideo,
  lockLandscapePlaybackOrientation,
} from '@/utils/fullscreen';
import Hls from 'hls.js';
import {
  createMemberPaymentTicket,
  createMemberRechargeOrder,
  fetchDramaDetail,
  fetchDramaList,
  fetchMemberPaymentOptions,
  isInsufficientPointsError,
  queryMemberRechargeOrder,
  recordDramaWatch,
  setDramaFavorite,
  unlockDramaEpisode,
  type DramaCard,
  type DramaDetail,
  type DramaEpisode,
} from '@/api/member';
import {
  ensureMemberAccessToken,
  readMemberSession,
  refreshCurrentMember,
  type MemberSession,
} from '@/utils/memberSession';
import { isNativeAppRuntime } from '@/utils/nativeApp';
import { shouldUseMobileExperience } from '@/utils/device';
import { loadSubtitleCues, selectInterfaceSubtitle, subtitleTextAt } from '@/utils/subtitlePlayback';
import CinemaRechargeModal from '../components/RechargeModal';
import { ShareRewardDialog } from '../components/ShareRewardFloat';

interface TrendingVideo {
  id: string;
  title: string;
  description: string;
  coverUrl: string;
  videoUrl: string;
  quality: string;
  category: string;
  duration: string;
  views: number;
  likes: number;
  tag?: string;
  episodeTag: string;
  username: string;
  userAvatar: string;
  totalEpisodes: number;
  unlockedCount: number;
}

interface QualityOption {
  id: string;
  label: string;
  level?: number;
  url?: string;
}

type SlideDirection = 'next' | 'previous';

interface SlideTransition {
  direction: SlideDirection;
  previousVideo: TrendingVideo;
  sequence: number;
}

function nextLockedEpisode(detail: DramaDetail | null) {
  return detail?.episodes.find((episode) => episode.episodeNo > 1 && !episode.isUnlocked) ?? null;
}

const videoMutedPreferenceKey = 'mfantasy_video_muted_preference';
const soundGuideSeenKey = 'cinema:hot-sound-guide-seen';
type VideoMutedPreference = 'muted' | 'unmuted' | null;

function readStoredMutedPreference(): VideoMutedPreference {
  if (typeof window === 'undefined') return null;
  const value = window.localStorage.getItem(videoMutedPreferenceKey);
  return value === 'muted' || value === 'unmuted' ? value : null;
}

function readMutedPreference() {
  if (isNativeAppRuntime() && readStoredMutedPreference() !== 'muted') {
    return false;
  }

  // Browsers only guarantee autoplay for muted media. Sound is restored after
  // a user changes videos, unless they explicitly chose to keep it muted.
  return true;
}

function readDesktopViewport() {
  return typeof window !== 'undefined' && !shouldUseMobileExperience();
}

function writeMutedPreference(isMuted: boolean) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(videoMutedPreferenceKey, isMuted ? 'muted' : 'unmuted');
}

function shouldShowSoundGuide() {
  if (typeof window === 'undefined' || isNativeAppRuntime()) return false;
  return window.localStorage.getItem(soundGuideSeenKey) !== '1';
}

function formatCount(n: number) {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return n.toLocaleString();
}

function randomLikeBoost() {
  return 3000 + Math.floor(Math.random() * 501);
}

interface HotPlaybackRequest {
  dramaId: number;
  episodeId: number;
  currentTime: number;
  playbackRate: number;
  muted: boolean | null;
  landscape: boolean;
}

function readHotPlaybackRequest(): HotPlaybackRequest {
  if (typeof window === 'undefined') {
    return { dramaId: 0, episodeId: 0, currentTime: 0, playbackRate: 1, muted: null, landscape: false };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    dramaId: Math.max(0, Number(params.get('video')) || 0),
    episodeId: Math.max(0, Number(params.get('episode')) || 0),
    currentTime: Math.max(0, Number(params.get('time')) || 0),
    playbackRate: Math.min(2, Math.max(0.5, Number(params.get('rate')) || 1)),
    muted: params.has('muted') ? params.get('muted') === '1' : null,
    landscape: params.get('landscape') === '1',
  };
}

function mapTrendingVideo(row: DramaCard, index: number, episode?: DramaEpisode): TrendingVideo {
  const activeEpisode = episode || row.currentEpisode;
  return {
    id: String(row.id),
    title: row.title,
    description: row.intro || row.shareDesc || '',
    coverUrl: row.coverUrl,
    videoUrl: activeEpisode?.videoUrl || '',
    quality: activeEpisode?.quality || '',
    category: row.categoryName || '短剧',
    duration: activeEpisode?.durationSeconds
      ? `${Math.floor(activeEpisode.durationSeconds / 60)}:${String(activeEpisode.durationSeconds % 60).padStart(2, '0')}`
      : '--:--',
    views: Number(row.viewCount) || 0,
    likes: (Number(row.favoriteCount) || 0) + randomLikeBoost(),
    tag: Number(row.viewCount) > 0 && index < 3 ? '热门' : undefined,
    episodeTag: `共${row.totalEpisodes}集`,
    username: '黄果TV',
    userAvatar: row.coverUrl,
    totalEpisodes: Number(row.totalEpisodes) || 0,
    unlockedCount: Number(row.unlockedCount) || 0,
  };
}

export default function CinemaMyListPage() {
  const { t, i18n } = useTranslation('cinema');
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const mobileSlideRef = useRef<HTMLDivElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const qualityOptionsRef = useRef<QualityOption[]>([]);
  const mobileProgressRef = useRef<HTMLDivElement>(null);
  const desktopProgressRef = useRef<HTMLDivElement>(null);
  const mobileSeekingRef = useRef(false);
  const seekingRef = useRef(false);
  const gestureLockRef = useRef(false);
  const transitionTimerRef = useRef<number | null>(null);
  const mobileTouchStartYRef = useRef<number | null>(null);
  const mobileSwipeRef = useRef(false);
  const recordedWatchRef = useRef('');
  const hotPlaybackRequestRef = useRef<HotPlaybackRequest>(readHotPlaybackRequest());
  const hotPlaybackSeekAppliedRef = useRef(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [slideTransition, setSlideTransition] = useState<SlideTransition | null>(null);
  const [likedSet, setLikedSet] = useState<Set<string>>(new Set());
  const [muted, setMuted] = useState(() => hotPlaybackRequestRef.current.muted ?? readMutedPreference());
  const [desktopViewport, setDesktopViewport] = useState(readDesktopViewport);
  const [mobilePlaying, setMobilePlaying] = useState(true);
  const [desktopPlaying, setDesktopPlaying] = useState(true);
  const [mobileProgress, setMobileProgress] = useState(0);
  const [desktopProgress, setDesktopProgress] = useState(0);
  const [desktopHovering, setDesktopHovering] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState<'quality' | 'speed' | null>(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [qualityMode, setQualityMode] = useState('auto');
  const [qualityOptions, setQualityOptions] = useState<QualityOption[]>([
    { id: 'auto', label: 'auto' },
  ]);
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState<TrendingVideo[]>([]);
  const [activeDetail, setActiveDetail] = useState<DramaDetail | null>(null);
  const [member, setMember] = useState<MemberSession>(() => readMemberSession());
  const [episodeDialogOpen, setEpisodeDialogOpen] = useState(false);
  const [playingEpisodeId, setPlayingEpisodeId] = useState('');
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [playbackEndedDialogOpen, setPlaybackEndedDialogOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showInsufficient, setShowInsufficient] = useState(false);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [shareRewardOpen, setShareRewardOpen] = useState(false);
  const [favoriteUpdating, setFavoriteUpdating] = useState(false);
  const [soundGuideOpen, setSoundGuideOpen] = useState(shouldShowSoundGuide);
  const [subtitleText, setSubtitleText] = useState('');

  useEffect(() => {
    let cancelled = false;
    void ensureMemberAccessToken()
      .then(async (token) => {
        const rows = await fetchDramaList(token || undefined, { scene: 'trending' });
        const request = hotPlaybackRequestRef.current;
        let nextVideos = rows.map((row, index) => mapTrendingVideo(row, index));

        if (request.dramaId > 0) {
          try {
            const detail = await fetchDramaDetail(request.dramaId, token || undefined);
            const requestedEpisode = detail.episodes.find((episode) => episode.id === request.episodeId)
              || detail.currentEpisode;
            nextVideos = [
              mapTrendingVideo(detail, 0, requestedEpisode),
              ...nextVideos.filter((video) => Number(video.id) !== request.dramaId),
            ];
            if (!cancelled) {
              setActiveDetail(detail);
              setPlayingEpisodeId(String(requestedEpisode?.id || ''));
              setPlaybackRate(request.playbackRate);
            }
          } catch {
            const requestedIndex = nextVideos.findIndex((video) => Number(video.id) === request.dramaId);
            if (requestedIndex > 0) {
              const [requestedVideo] = nextVideos.splice(requestedIndex, 1);
              nextVideos.unshift(requestedVideo);
            }
          }
        }
        return nextVideos;
      })
      .then((nextVideos) => {
        if (cancelled) return;
        setVideos(nextVideos);
        setActiveIdx(0);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    const syncViewport = () => setDesktopViewport(!shouldUseMobileExperience());
    syncViewport();
    window.addEventListener('resize', syncViewport, { passive: true });
    window.addEventListener('orientationchange', syncViewport, { passive: true });
    return () => {
      window.removeEventListener('resize', syncViewport);
      window.removeEventListener('orientationchange', syncViewport);
    };
  }, []);

  const openSoundForNavigation = useCallback(() => {
    // A manual mute is the only action that prevents automatic sound on switching.
    if (readStoredMutedPreference() === 'muted') return;
    const activeVideo = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    if (activeVideo) activeVideo.muted = false;
    setMuted(false);
  }, [desktopViewport]);

  const dismissSoundGuide = useCallback(() => {
    setSoundGuideOpen(false);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(soundGuideSeenKey, '1');
    }
  }, []);

  const handleManualMuteToggle = useCallback(() => {
    dismissSoundGuide();
    setMuted((previous) => {
      const next = !previous;
      const activeVideo = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
      if (activeVideo) activeVideo.muted = next;
      writeMutedPreference(next);
      return next;
    });
  }, [desktopViewport, dismissSoundGuide]);

  const enableSoundFromGuide = useCallback(() => {
    const activeVideo = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    if (activeVideo) {
      activeVideo.muted = false;
      void activeVideo.play().catch(() => undefined);
    }
    writeMutedPreference(false);
    setMuted(false);
    dismissSoundGuide();
  }, [desktopViewport, dismissSoundGuide]);

  const toggleLike = (id: string) => {
    setLikedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const switchVideo = useCallback((direction: SlideDirection) => {
    if (videos.length < 2 || gestureLockRef.current) return;
    gestureLockRef.current = true;
    openSoundForNavigation();

    const previousVideo = videos[activeIdx];
    const offset = direction === 'next' ? 1 : -1;
    const nextIdx = (activeIdx + offset + videos.length) % videos.length;
    setSlideTransition({ direction, previousVideo, sequence: Date.now() });
    setActiveIdx(nextIdx);

    if (transitionTimerRef.current !== null) {
      window.clearTimeout(transitionTimerRef.current);
    }
    transitionTimerRef.current = window.setTimeout(() => {
      setSlideTransition(null);
      gestureLockRef.current = false;
      transitionTimerRef.current = null;
    }, 360);
  }, [activeIdx, openSoundForNavigation, videos]);

  const goPrev = useCallback(() => switchVideo('previous'), [switchVideo]);
  const goNext = useCallback(() => switchVideo('next'), [switchVideo]);

  const handleMobileTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    if (gestureLockRef.current) return;
    mobileTouchStartYRef.current = event.touches[0]?.clientY ?? null;
    const slide = mobileSlideRef.current;
    if (slide) slide.style.transition = 'none';
  }, []);

  const handleMobileTouchMove = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const startY = mobileTouchStartYRef.current;
    const currentY = event.touches[0]?.clientY;
    const slide = mobileSlideRef.current;
    if (startY === null || currentY === undefined || !slide || mobileSeekingRef.current) return;
    const delta = currentY - startY;
    slide.style.transform = `translate3d(0, ${delta * 0.72}px, 0)`;
    slide.style.opacity = String(Math.max(0.78, 1 - Math.abs(delta) / 900));
  }, []);

  const handleMobileTouchEnd = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    const startY = mobileTouchStartYRef.current;
    const endY = event.changedTouches[0]?.clientY;
    mobileTouchStartYRef.current = null;
    const slide = mobileSlideRef.current;
    if (slide) {
      slide.style.transition = 'transform 180ms ease-out, opacity 180ms ease-out';
      slide.style.transform = '';
      slide.style.opacity = '';
    }
    if (startY === null || endY === undefined || Math.abs(endY - startY) < 48 || !videos.length) return;

    mobileSwipeRef.current = true;
    window.setTimeout(() => { mobileSwipeRef.current = false; }, 0);
    if (endY < startY) goNext();
    else goPrev();
  }, [goNext, goPrev, videos.length]);

  const handlePlaybackEnded = useCallback(() => {
    exitVideoFullscreen(mobileVideoRef.current || desktopVideoRef.current);
    setMobilePlaying(false);
    setDesktopPlaying(false);
    setPlaybackEndedDialogOpen(true);
  }, []);

  const toggleDesktopPlay = useCallback(() => {
    const video = desktopVideoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().then(() => setDesktopPlaying(true)).catch(() => setDesktopPlaying(false));
      return;
    }
    video.pause();
    setDesktopPlaying(false);
  }, []);

  const toggleMobilePlay = useCallback(() => {
    const video = mobileVideoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().then(() => setMobilePlaying(true)).catch(() => setMobilePlaying(false));
      return;
    }
    video.pause();
    setMobilePlaying(false);
  }, []);

  const seekMobileToClientX = useCallback((clientX: number) => {
    const video = mobileVideoRef.current;
    const bar = mobileProgressRef.current;
    if (!video || !bar || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
    setMobileProgress(ratio * 100);
  }, []);

  const handleMobileProgressDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    mobileSeekingRef.current = true;
    mobileTouchStartYRef.current = null;
    event.currentTarget.setPointerCapture(event.pointerId);
    seekMobileToClientX(event.clientX);
  }, [seekMobileToClientX]);

  const handleMobileProgressMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!mobileSeekingRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    seekMobileToClientX(event.clientX);
  }, [seekMobileToClientX]);

  const finishMobileSeek = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!mobileSeekingRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    mobileSeekingRef.current = false;
    mobileTouchStartYRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const seekDesktopToClientX = useCallback((clientX: number) => {
    const video = desktopVideoRef.current;
    const bar = desktopProgressRef.current;
    if (!video || !bar || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
    setDesktopProgress(ratio * 100);
  }, []);

  const handleDesktopProgressDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      seekingRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
      seekDesktopToClientX(event.clientX);
    },
    [seekDesktopToClientX],
  );

  const handleDesktopProgressMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!seekingRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      seekDesktopToClientX(event.clientX);
    },
    [seekDesktopToClientX],
  );

  const finishDesktopSeek = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!seekingRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    seekingRef.current = false;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleDesktopWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      if (Math.abs(event.deltaY) < 36) return;
      event.preventDefault();
      if (event.deltaY > 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  // Keyboard navigation for desktop
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') goPrev();
      if (e.key === 'ArrowDown') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  useEffect(() => () => {
    if (transitionTimerRef.current !== null) {
      window.clearTimeout(transitionTimerRef.current);
    }
  }, []);

  const currentVideo = videos[activeIdx];
  const unlockEpisode = nextLockedEpisode(activeDetail);
  const activeDetailMatchesCurrent = Boolean(
    activeDetail && currentVideo && activeDetail.id === Number(currentVideo.id),
  );
  const currentFavorite = activeDetailMatchesCurrent && Boolean(activeDetail?.isFavorite);
  const displayUnlockedCount = activeDetailMatchesCurrent
    ? activeDetail!.unlockedCount
    : currentVideo?.unlockedCount || 0;
  const displayTotalEpisodes = activeDetailMatchesCurrent
    ? activeDetail!.totalEpisodes
    : currentVideo?.totalEpisodes || 0;
  const activePlayingEpisode = activeDetailMatchesCurrent
    ? activeDetail!.episodes.find((episode) => String(episode.id) === playingEpisodeId)
      || activeDetail!.currentEpisode
    : null;
  const activeSubtitleTrack = selectInterfaceSubtitle(
    activePlayingEpisode?.subtitleTracks || [],
    i18n.resolvedLanguage || i18n.language,
  );
  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  useEffect(() => {
    const video = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    setSubtitleText('');
    if (!video || !activeSubtitleTrack) return undefined;

    let cancelled = false;
    let cues: Awaited<ReturnType<typeof loadSubtitleCues>> = [];
    const updateSubtitle = () => {
      if (!cancelled) setSubtitleText(subtitleTextAt(cues, video.currentTime, desktopViewport ? 72 : 34));
    };
    const loadActiveSubtitle = () => {
      void loadSubtitleCues(activeSubtitleTrack)
        .then((loaded) => {
          if (cancelled) return;
          cues = loaded;
          updateSubtitle();
        })
        .catch(() => undefined);
    };
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) loadActiveSubtitle();
    else video.addEventListener('canplay', loadActiveSubtitle, { once: true });
    video.addEventListener('timeupdate', updateSubtitle);
    video.addEventListener('seeking', updateSubtitle);
    return () => {
      cancelled = true;
      video.removeEventListener('canplay', loadActiveSubtitle);
      video.removeEventListener('timeupdate', updateSubtitle);
      video.removeEventListener('seeking', updateSubtitle);
    };
  }, [activeSubtitleTrack?.id, activeSubtitleTrack?.url, activeSubtitleTrack?.version, currentVideo?.id, desktopViewport]);

  useEffect(() => {
    const video = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    const videoUrl = currentVideo?.videoUrl || '';
    if (!video || !videoUrl) return;

    hlsRef.current?.destroy();
    hlsRef.current = null;
    qualityOptionsRef.current = [{ id: 'auto', label: 'auto' }];
    setQualityOptions(qualityOptionsRef.current);
    setQualityMode('auto');

    const setPlaying = (playing: boolean) => {
      if (desktopViewport) setDesktopPlaying(playing);
      else setMobilePlaying(playing);
    };
    const playVideo = () => {
      video.playbackRate = playbackRate;
      void video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    };
    const isHlsVideo = /\.m3u8(?:$|[?#])/i.test(videoUrl);

    if (isHlsVideo && Hls.isSupported()) {
      video.removeAttribute('src');
      const hls = new Hls({ enableWorker: true, startLevel: -1 });
      hlsRef.current = hls;
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(videoUrl));
      hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
        const levels: QualityOption[] = data.levels.map((level, index) => ({
          id: `level-${index}`,
          label: level.name?.trim() || (level.height ? `${level.height}P` : `线路${index + 1}`),
          level: index,
        }));
        qualityOptionsRef.current = [{ id: 'auto', label: 'auto', level: -1 }, ...levels];
        setQualityOptions(qualityOptionsRef.current);
        playVideo();
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        else setPlaying(false);
      });
      return () => {
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
      };
    }

    video.src = videoUrl;
    video.load();
    video.addEventListener('loadedmetadata', playVideo, { once: true });
    if (!isHlsVideo) {
      const label = currentVideo.quality.trim() || 'source';
      qualityOptionsRef.current = [{ id: 'source', label }];
      setQualityOptions(qualityOptionsRef.current);
      setQualityMode('source');
      return () => video.removeEventListener('loadedmetadata', playVideo);
    }

    const controller = new AbortController();
    void fetch(videoUrl, { signal: controller.signal })
      .then((response) => response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`)))
      .then((manifest) => {
        const lines = manifest.split(/\r?\n/);
        const choices: QualityOption[] = [{ id: 'auto', label: 'auto', url: videoUrl }];
        for (let index = 0; index < lines.length; index += 1) {
          const info = lines[index].trim();
          if (!info.startsWith('#EXT-X-STREAM-INF:')) continue;
          const path = lines[index + 1]?.trim();
          if (!path || path.startsWith('#')) continue;
          const name = info.match(/(?:^|,)NAME="?([^",]+)"?/i)?.[1];
          const height = info.match(/RESOLUTION=\d+x(\d+)/i)?.[1];
          choices.push({
            id: `variant-${choices.length}`,
            label: name?.trim() || (height ? `${height}P` : `线路${choices.length}`),
            url: new URL(path, videoUrl).toString(),
          });
        }
        qualityOptionsRef.current = choices;
        setQualityOptions(choices);
      })
      .catch(() => undefined);

    return () => {
      controller.abort();
      video.removeEventListener('loadedmetadata', playVideo);
    };
  }, [currentVideo?.quality, currentVideo?.videoUrl, desktopViewport]);

  useEffect(() => {
    const option = qualityOptionsRef.current.find((item) => item.id === qualityMode);
    const video = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    const hls = hlsRef.current;
    if (!option || !video) return;

    if (hls && typeof option.level === 'number') {
      hls.currentLevel = option.level;
      return;
    }
    if (!option.url || video.currentSrc === option.url) return;

    const currentTime = video.currentTime;
    const wasPaused = video.paused;
    video.src = option.url;
    video.load();
    video.addEventListener('loadedmetadata', () => {
      video.currentTime = Math.min(currentTime, Number.isFinite(video.duration) ? video.duration : currentTime);
      video.playbackRate = playbackRate;
      if (!wasPaused) void video.play().catch(() => undefined);
    }, { once: true });
  }, [desktopViewport, playbackRate, qualityMode]);

  useEffect(() => {
    const video = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    const mobilePlayback = !desktopViewport || isNativeAppRuntime();
    if (!video || !mobilePlayback) return undefined;

    const syncPlaybackOrientation = () => {
      if (isLandscapeVideo(video)) {
        lockLandscapePlaybackOrientation();
        return;
      }
      exitVideoFullscreen(video);
    };

    if (video.readyState >= 1) syncPlaybackOrientation();
    video.addEventListener('loadedmetadata', syncPlaybackOrientation);
    return () => video.removeEventListener('loadedmetadata', syncPlaybackOrientation);
  }, [currentVideo?.id, currentVideo?.videoUrl, desktopViewport]);

  useEffect(() => {
    const request = hotPlaybackRequestRef.current;
    if (request.landscape && (!desktopViewport || isNativeAppRuntime())) {
      lockLandscapePlaybackOrientation();
    }
  }, [desktopViewport]);

  useEffect(() => () => {
    exitVideoFullscreen(mobileVideoRef.current || desktopVideoRef.current);
  }, []);

  useEffect(() => {
    for (const video of [mobileVideoRef.current, desktopVideoRef.current]) {
      if (video) video.playbackRate = playbackRate;
    }
  }, [playbackRate, currentVideo?.id]);

  useEffect(() => {
    setSettingsOpen(false);
    setQualityMode('auto');
  }, [currentVideo?.id]);

  useEffect(() => {
    if (!currentVideo || !activePlayingEpisode || activePlayingEpisode.episodeNo <= 1 || !activePlayingEpisode.isUnlocked) {
      return;
    }
    const watchKey = `${currentVideo.id}:${activePlayingEpisode.id}`;
    if (recordedWatchRef.current === watchKey) return;

    void ensureMemberAccessToken().then((token) => {
      if (!token) return;
      recordedWatchRef.current = watchKey;
      void recordDramaWatch(Number(currentVideo.id), activePlayingEpisode.id, token).catch(() => {
        recordedWatchRef.current = '';
      });
    });
  }, [activePlayingEpisode?.episodeNo, activePlayingEpisode?.id, activePlayingEpisode?.isUnlocked, currentVideo?.id]);

  useEffect(() => {
    const dramaId = Number(currentVideo?.id);
    if (!dramaId) {
      setActiveDetail(null);
      return;
    }

    let cancelled = false;
    void ensureMemberAccessToken()
      .then((token) => fetchDramaDetail(dramaId, token || undefined))
      .then((detail) => {
        if (!cancelled) {
          const request = hotPlaybackRequestRef.current;
          const requestedEpisode = request.dramaId === dramaId
            ? detail.episodes.find((episode) => episode.id === request.episodeId)
            : undefined;
          const episode = requestedEpisode || detail.currentEpisode;
          setActiveDetail(detail);
          setPlayingEpisodeId(String(episode?.id || ''));
          if (requestedEpisode) {
            setVideos((previous) => previous.map((video) => video.id === String(dramaId)
              ? mapTrendingVideo(detail, 0, requestedEpisode)
              : video));
          }
        }
      })
      .catch(() => {
        if (!cancelled) setActiveDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentVideo?.id]);

  const requestUnlock = useCallback(() => {
    if (!currentVideo || !unlockEpisode) return;
    void ensureMemberAccessToken().then((token) => {
      if (!token) {
        navigate('/login');
        return;
      }
      setShowInsufficient(false);
      setUnlockDialogOpen(true);
    });
  }, [currentVideo, navigate, unlockEpisode]);

  const openEpisodeDialog = useCallback(() => {
    const dramaId = Number(currentVideo?.id);
    if (!dramaId) return;
    setEpisodeDialogOpen(true);
    if (activeDetail?.id === dramaId) return;
    void ensureMemberAccessToken()
      .then((token) => fetchDramaDetail(dramaId, token || undefined))
      .then((detail) => {
        if (Number(currentVideo?.id) !== dramaId) return;
        setActiveDetail(detail);
        setPlayingEpisodeId(String(detail.currentEpisode?.id || ''));
      })
      .catch(() => setEpisodeDialogOpen(false));
  }, [activeDetail?.id, currentVideo?.id]);

  const toggleCurrentFavorite = useCallback(() => {
    if (!currentVideo || favoriteUpdating) return;
    void ensureMemberAccessToken().then(async (token) => {
      if (!token) {
        navigate('/login');
        return;
      }
      const favorite = !(activeDetail?.id === Number(currentVideo.id) && activeDetail.isFavorite);
      setFavoriteUpdating(true);
      try {
        await setDramaFavorite(Number(currentVideo.id), favorite, token);
        setActiveDetail((detail) => detail?.id === Number(currentVideo.id)
          ? { ...detail, isFavorite: favorite }
          : detail);
      } finally {
        setFavoriteUpdating(false);
      }
    }).catch(() => undefined);
  }, [activeDetail, currentVideo, favoriteUpdating, navigate]);

  const playUnlockedEpisode = useCallback((episode: DramaDetail['episodes'][number]) => {
    if (!currentVideo || !episode.isUnlocked) return;
    setPlayingEpisodeId(String(episode.id));
    setVideos((previous) => previous.map((video) => video.id === currentVideo.id
      ? {
          ...video,
          videoUrl: episode.videoUrl || video.videoUrl,
          quality: episode.quality || '',
          duration: episode.durationSeconds
            ? `${Math.floor(episode.durationSeconds / 60)}:${String(episode.durationSeconds % 60).padStart(2, '0')}`
            : video.duration,
        }
      : video));
    setEpisodeDialogOpen(false);
  }, [currentVideo]);

  const confirmUnlock = useCallback(() => {
    if (!currentVideo || !unlockEpisode || unlocking) return;
    setUnlocking(true);
    void ensureMemberAccessToken()
      .then(async (token) => {
        if (!token) {
          navigate('/login');
          return;
        }
        const latestMember = await refreshCurrentMember();
        setMember(latestMember);
        if (latestMember.pointsBalance < unlockEpisode.unlockPoints) {
          setShowInsufficient(true);
          window.setTimeout(() => setRechargeOpen(true), 1000);
          return;
        }
        const result = await unlockDramaEpisode(Number(currentVideo.id), token);
        setActiveDetail(result.drama);
        setPlayingEpisodeId(String(result.drama.currentEpisode?.id || ''));
        setVideos((previous) => previous.map((video) => video.id === currentVideo.id
          ? {
              ...video,
              videoUrl: result.drama.currentEpisode?.videoUrl || video.videoUrl,
              quality: result.drama.currentEpisode?.quality || '',
              totalEpisodes: result.drama.totalEpisodes,
              unlockedCount: result.drama.unlockedCount,
              duration: result.drama.currentEpisode?.durationSeconds
                ? `${Math.floor(result.drama.currentEpisode.durationSeconds / 60)}:${String(result.drama.currentEpisode.durationSeconds % 60).padStart(2, '0')}`
                : video.duration,
            }
          : video));
        setMember(await refreshCurrentMember());
        setEpisodeDialogOpen(false);
        setUnlockDialogOpen(false);
      })
      .catch((error) => {
        if (isInsufficientPointsError(error)) {
          setShowInsufficient(true);
          window.setTimeout(() => setRechargeOpen(true), 1000);
        }
      })
      .finally(() => setUnlocking(false));
  }, [currentVideo, navigate, unlockEpisode, unlocking]);

  useEffect(() => {
    const video = desktopVideoRef.current;
    if (!video || !currentVideo) return;
    setPlaybackEndedDialogOpen(false);
    video.currentTime = 0;
    setDesktopProgress(0);
    setDesktopPlaying(true);
    const timer = window.setTimeout(() => {
      void video.play().then(() => setDesktopPlaying(true)).catch(() => setDesktopPlaying(false));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [currentVideo?.id]);

  useEffect(() => {
    const video = mobileVideoRef.current;
    if (!video || !currentVideo) return;
    setPlaybackEndedDialogOpen(false);
    video.currentTime = 0;
    setMobileProgress(0);
    setMobilePlaying(true);
    const timer = window.setTimeout(() => {
      void video.play().then(() => setMobilePlaying(true)).catch(() => setMobilePlaying(false));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [currentVideo?.id]);

  useEffect(() => {
    const request = hotPlaybackRequestRef.current;
    if (
      hotPlaybackSeekAppliedRef.current
      || request.dramaId <= 0
      || request.dramaId !== Number(currentVideo?.id)
      || request.episodeId !== Number(activePlayingEpisode?.id)
    ) {
      return undefined;
    }

    const video = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    if (!video) return undefined;

    const restorePlayback = () => {
      const duration = Number.isFinite(video.duration) && video.duration > 0
        ? video.duration
        : request.currentTime;
      const restoredTime = Math.min(request.currentTime, Math.max(0, duration - 0.05));
      video.currentTime = restoredTime;
      video.playbackRate = request.playbackRate;
      if (request.muted !== null) {
        video.muted = request.muted;
        setMuted(request.muted);
      }
      setPlaybackRate(request.playbackRate);
      const progress = duration > 0 ? (restoredTime / duration) * 100 : 0;
      if (desktopViewport) setDesktopProgress(progress);
      else setMobileProgress(progress);
      hotPlaybackSeekAppliedRef.current = true;
      void video.play().catch(() => undefined);
    };

    if (video.readyState >= 1) {
      restorePlayback();
      return undefined;
    }

    video.addEventListener('loadedmetadata', restorePlayback, { once: true });
    return () => video.removeEventListener('loadedmetadata', restorePlayback);
  }, [activePlayingEpisode?.id, currentVideo?.id, currentVideo?.videoUrl, desktopViewport]);

  useEffect(() => {
    const video = mobileVideoRef.current;
    if (!video) return;
    const updateProgress = () => {
      if (!video.duration || mobileSeekingRef.current) return;
      setMobileProgress((video.currentTime / video.duration) * 100);
    };
    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('loadedmetadata', updateProgress);
    return () => {
      video.removeEventListener('timeupdate', updateProgress);
      video.removeEventListener('loadedmetadata', updateProgress);
    };
  }, [currentVideo?.id]);

  useEffect(() => {
    const video = desktopVideoRef.current;
    if (!video) return;
    const updateProgress = () => {
      if (!video.duration) return;
      setDesktopProgress((video.currentTime / video.duration) * 100);
    };
    const markPlaying = () => setDesktopPlaying(true);
    const markPaused = () => setDesktopPlaying(false);
    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('play', markPlaying);
    video.addEventListener('pause', markPaused);
    return () => {
      video.removeEventListener('timeupdate', updateProgress);
      video.removeEventListener('play', markPlaying);
      video.removeEventListener('pause', markPaused);
    };
  }, [currentVideo?.id]);

  const selectPlayerQuality = (option: QualityOption) => {
    const video = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    const hls = hlsRef.current;

    if (hls && typeof option.level === 'number') {
      hls.currentLevel = option.level;
    } else if (video && option.url && video.currentSrc !== option.url) {
      const currentTime = video.currentTime;
      const wasPaused = video.paused;
      video.src = option.url;
      video.load();
      video.addEventListener('loadedmetadata', () => {
        video.currentTime = Math.min(currentTime, Number.isFinite(video.duration) ? video.duration : currentTime);
        video.playbackRate = playbackRate;
        if (!wasPaused) void video.play().catch(() => undefined);
      }, { once: true });
    }

    setQualityMode(option.id);
    setSettingsOpen(false);
    setMobileSettingsOpen(null);
  };

  const selectPlayerSpeed = (speed: number) => {
    const video = desktopViewport ? desktopVideoRef.current : mobileVideoRef.current;
    if (video) video.playbackRate = speed;
    setPlaybackRate(speed);
    setSettingsOpen(false);
    setMobileSettingsOpen(null);
  };

  const activeQualityOption = qualityOptions.find((option) => option.id === qualityMode) || qualityOptions[0];
  const activeQualityLabel = !activeQualityOption || activeQualityOption.id === 'auto'
    ? t('cinema_playerAuto')
    : activeQualityOption.label === 'source'
      ? t('cinema_playerSource')
      : activeQualityOption.label;

  const renderSettingsControl = (mobile = false) => (
    <div className="relative flex flex-col items-center">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          if (mobile) {
            setMobileSettingsOpen((open) => open === 'quality' ? null : 'quality');
          } else {
            setSettingsOpen((open) => !open);
          }
        }}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-background-950/60 text-foreground-200 backdrop-blur-sm transition-colors hover:bg-background-950/80 md:h-9 md:w-9"
        aria-label={t('cinema_playerSettings')}
        aria-expanded={settingsOpen}
      >
        <i className="ri-settings-3-line text-sm md:text-base" />
      </button>
      {!mobile && settingsOpen && (
        <div
          className="absolute bottom-11 right-0 z-40 w-44 rounded-xl border border-white/10 bg-background-900/95 p-3 text-xs text-foreground-200 shadow-2xl backdrop-blur-md"
          onClick={(event) => event.stopPropagation()}
        >
          <p className="mb-2 font-semibold text-foreground-400">{t('cinema_playerQuality')}</p>
          <div className="flex flex-wrap gap-1.5">
            {qualityOptions.map((option) => {
              const active = qualityMode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    selectPlayerQuality(option);
                  }}
                  className={`rounded-md px-2 py-1 font-medium transition-colors ${active ? 'bg-accent-500 text-background-950' : 'bg-foreground-50/10 text-foreground-300 hover:bg-foreground-50/20'}`}
                >
                  {option.id === 'auto' ? t('cinema_playerAuto') : option.label === 'source' ? t('cinema_playerSource') : option.label}
                </button>
              );
            })}
          </div>
          <p className="mb-2 mt-3 font-semibold text-foreground-400">{t('cinema_playerSpeed')}</p>
          <div className="flex flex-wrap gap-1.5">
            {speedOptions.map((speed) => {
              const active = playbackRate === speed;
              return (
                <button
                  key={speed}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    selectPlayerSpeed(speed);
                  }}
                  className={`rounded-md px-2 py-1 font-medium transition-colors ${active ? 'bg-accent-500 text-background-950' : 'bg-foreground-50/10 text-foreground-300 hover:bg-foreground-50/20'}`}
                >
                  {speed}x
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  if (videos.length === 0) {
    return (
      <div className="h-[100vh] flex items-center justify-center bg-background-950">
        <div className="text-center px-6">
          <span className="w-20 h-20 flex items-center justify-center mx-auto mb-5 rounded-full bg-foreground-50/5">
            <i className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-fire-line'} text-4xl text-foreground-600`}></i>
          </span>
          <h2 className="text-lg font-bold font-heading text-foreground-300 mb-2">{t('cinema_myList')}</h2>
          <p className="text-sm text-foreground-600 mb-6">{loading ? '加载中...' : t('cinema_emptyMyList')}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 rounded-full text-sm font-medium text-background-950 bg-accent-500 hover:bg-accent-500/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('cinema_navHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="md:pt-0">
      {/* ====== MOBILE: one player, with swipe switching its current data ====== */}
      <div
        ref={containerRef}
        onTouchStart={handleMobileTouchStart}
        onTouchMove={handleMobileTouchMove}
        onTouchEnd={handleMobileTouchEnd}
        className={`${desktopViewport ? 'hidden' : 'block'} relative h-[calc(100dvh-64px)] overflow-hidden touch-none overscroll-none bg-black`}
      >
        {slideTransition && (
          <div
            key={`mobile-outgoing-${slideTransition.sequence}`}
            className={`pointer-events-none absolute inset-0 z-[5] bg-black ${slideTransition.direction === 'next' ? 'cinema-slide-exit-up' : 'cinema-slide-exit-down'}`}
          >
            <img src={slideTransition.previousVideo.coverUrl} alt="" className="h-full w-full object-contain" />
          </div>
        )}
        {currentVideo && (() => {
          return (
            <div
              ref={mobileSlideRef}
              key={currentVideo.id}
              className={`relative z-10 h-full w-full overflow-hidden will-change-transform ${slideTransition ? (slideTransition.direction === 'next' ? 'cinema-slide-enter-up' : 'cinema-slide-enter-down') : ''}`}
            >
              <video
                ref={mobileVideoRef}
                poster={currentVideo.coverUrl}
                className="absolute inset-0 m-auto block h-full w-full max-h-full max-w-full object-contain bg-black"
                autoPlay
                muted={desktopViewport ? true : muted}
                playsInline
                onEnded={handlePlaybackEnded}
                onClick={() => {
                  if (mobileSwipeRef.current) return;
                  toggleMobilePlay();
                }}
                onPlay={() => setMobilePlaying(true)}
                onPause={() => setMobilePlaying(false)}
              />
              {subtitleText && (
                <div className="pointer-events-none absolute inset-x-4 bottom-7 z-[18] flex justify-center">
                  <p className="max-w-[88%] whitespace-nowrap rounded-md bg-black/65 px-3 py-1.5 text-center text-[15px] font-medium leading-relaxed text-white shadow-lg sm:max-w-[72%]">
                    {subtitleText}
                  </p>
                </div>
              )}
              {!mobilePlaying && (
                <button
                  type="button"
                  onClick={toggleMobilePlay}
                  className="absolute left-1/2 top-1/2 z-20 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-background-950/65 text-accent-400 backdrop-blur-sm"
                  aria-label="Play"
                >
                  <i className="ri-play-fill text-3xl" />
                </button>
              )}

              {/* Bottom left: video info */}
              <div className="absolute bottom-9 left-4 right-28 z-10">
                <div className="flex items-center gap-2">
                  <h3 className="min-w-0 truncate text-base font-bold leading-snug text-foreground-50">{currentVideo.title}</h3>
                  {displayTotalEpisodes > 0 && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openEpisodeDialog();
                      }}
                      className="shrink-0 rounded-lg border border-[#F97316] bg-background-950/70 px-2 py-1 text-xs font-semibold text-[#F97316] backdrop-blur-sm transition-colors hover:bg-[#F97316] hover:text-background-950"
                      title={t('cinema_episodeList')}
                      aria-label={t('cinema_episodeList')}
                    >
                      {t('cinema_unlocked')} {displayUnlockedCount}/{displayTotalEpisodes}
                    </button>
                  )}
                </div>
              </div>

              {/* Right side: action buttons */}
              <div className="absolute right-3 top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2.5">
                <div className="relative">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleManualMuteToggle();
                    }}
                    className={`flex h-8 w-8 items-center justify-center rounded-full bg-background-950/55 text-foreground-100 backdrop-blur-sm transition-colors active:bg-accent-500 active:text-background-950 ${soundGuideOpen && muted ? 'ring-2 ring-accent-400 ring-offset-2 ring-offset-black/70 animate-pulse' : ''}`}
                    title={muted ? 'Unmute' : 'Mute'}
                    aria-label={muted ? '打开声音' : '关闭声音'}
                  >
                    <i className={muted ? 'ri-volume-mute-line text-base' : 'ri-volume-up-line text-base'} />
                  </button>
                  {soundGuideOpen && muted && (
                    <div
                      className="absolute right-11 top-0 z-40 w-44 animate-pulse rounded-lg border border-accent-400/60 bg-background-900 px-3 py-2.5 text-left shadow-xl shadow-black/40"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <span className="absolute -right-1.5 top-2.5 h-3 w-3 rotate-45 border-r border-t border-accent-400/60 bg-background-900" />
                      <button
                        type="button"
                        onClick={dismissSoundGuide}
                        className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center text-foreground-500 hover:text-foreground-100"
                        aria-label={t('cinema_soundGuideDismiss')}
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                      <p className="pr-4 text-xs font-semibold text-foreground-50">{t('cinema_soundGuideTitle')}</p>
                      <button
                        type="button"
                        onClick={enableSoundFromGuide}
                        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1.5 text-xs font-bold text-background-950"
                      >
                        <i className="ri-volume-up-line" />
                        {t('cinema_soundGuideAction')}
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={(event) => { event.stopPropagation(); goPrev(); }}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-background-950/55 text-foreground-100 backdrop-blur-sm transition-colors active:bg-accent-500 active:text-background-950"
                  aria-label="上一部"
                  title="上一部"
                >
                  <i className="ri-arrow-up-line text-base" />
                </button>

                <button
                  type="button"
                  onClick={(event) => { event.stopPropagation(); goNext(); }}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-background-950/55 text-foreground-100 backdrop-blur-sm transition-colors active:bg-accent-500 active:text-background-950"
                  aria-label="下一部"
                  title="下一部"
                >
                  <i className="ri-arrow-down-line text-base" />
                </button>

                {/* Favorite */}
                <button
                  type="button"
                  onClick={(event) => { event.stopPropagation(); toggleCurrentFavorite(); }}
                  disabled={favoriteUpdating}
                  className="flex flex-col items-center gap-0.5 cursor-pointer group"
                >
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full bg-background-950/40 text-base backdrop-blur-sm transition-colors ${currentFavorite ? 'text-accent-400' : 'text-foreground-100 group-hover:text-accent-400'}`}>
                    <i className={favoriteUpdating ? 'ri-loader-4-line animate-spin' : currentFavorite ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                  </span>
                  <span className="text-[9px] text-foreground-300 font-medium">{t('cinema_collect')}</span>
                </button>

                {/* Share */}
                <button onClick={() => setShareRewardOpen(true)} className="flex flex-col items-center gap-0.5 cursor-pointer group">
                  <span className="flex h-7 w-7 items-center justify-center [perspective:120px]">
                    <img
                      src="/images/reward-coin.webp"
                      alt=""
                      aria-hidden="true"
                      className="animate-coin-spin-y-forward h-7 w-7 object-contain drop-shadow-[0_2px_5px_rgba(245,184,65,0.55)]"
                    />
                  </span>
                  <span className="text-[9px] text-foreground-300 font-medium">{t('cinema_share')}</span>
                </button>
              </div>

              {/* Current quality and speed stay visible below Settings, aligned with the title. */}
              <div className="absolute bottom-9 right-3 z-20 flex items-center gap-1.5">
                <div className="relative">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setMobileSettingsOpen((open) => open === 'quality' ? null : 'quality');
                    }}
                    className={`max-w-[58px] truncate rounded-full border px-2 py-1 text-[10px] font-semibold backdrop-blur-sm transition-colors ${mobileSettingsOpen === 'quality' ? 'border-accent-500 bg-accent-500 text-background-950' : 'border-white/10 bg-background-950/70 text-foreground-200'}`}
                    aria-label={`${t('cinema_playerQuality')}: ${activeQualityLabel}`}
                    aria-expanded={mobileSettingsOpen === 'quality'}
                  >
                    {activeQualityLabel}
                  </button>
                  {mobileSettingsOpen === 'quality' && (
                    <div className="absolute bottom-8 right-0 z-40 min-w-[116px] rounded-xl border border-white/10 bg-background-900/95 p-2 shadow-2xl backdrop-blur-md" onClick={(event) => event.stopPropagation()}>
                      <p className="mb-1.5 whitespace-nowrap px-1 text-[10px] font-semibold text-foreground-400">{t('cinema_playerQuality')}</p>
                      <div className="flex flex-wrap justify-end gap-1">
                        {qualityOptions.map((option) => {
                          const active = qualityMode === option.id;
                          const label = option.id === 'auto' ? t('cinema_playerAuto') : option.label === 'source' ? t('cinema_playerSource') : option.label;
                          return <button key={option.id} type="button" onClick={(event) => { event.stopPropagation(); selectPlayerQuality(option); }} className={`rounded-md px-2 py-1 text-[10px] font-medium ${active ? 'bg-accent-500 text-background-950' : 'bg-foreground-50/10 text-foreground-300'}`}>{label}</button>;
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setMobileSettingsOpen((open) => open === 'speed' ? null : 'speed');
                    }}
                    className={`rounded-full border px-2 py-1 text-[10px] font-semibold backdrop-blur-sm transition-colors ${mobileSettingsOpen === 'speed' ? 'border-accent-500 bg-accent-500 text-background-950' : 'border-white/10 bg-background-950/70 text-foreground-200'}`}
                    aria-label={`${t('cinema_playerSpeed')}: ${playbackRate}x`}
                    aria-expanded={mobileSettingsOpen === 'speed'}
                  >
                    {playbackRate}x
                  </button>
                  {mobileSettingsOpen === 'speed' && (
                    <div className="absolute bottom-8 right-0 z-40 w-[126px] rounded-xl border border-white/10 bg-background-900/95 p-2 shadow-2xl backdrop-blur-md" onClick={(event) => event.stopPropagation()}>
                      <p className="mb-1.5 px-1 text-[10px] font-semibold text-foreground-400">{t('cinema_playerSpeed')}</p>
                      <div className="grid grid-cols-3 gap-1">
                        {speedOptions.map((speed) => <button key={speed} type="button" onClick={(event) => { event.stopPropagation(); selectPlayerSpeed(speed); }} className={`rounded-md px-1 py-1 text-[10px] font-medium ${playbackRate === speed ? 'bg-accent-500 text-background-950' : 'bg-foreground-50/10 text-foreground-300'}`}>{speed}x</button>)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom progress bar */}
              <div
                ref={mobileProgressRef}
                role="slider"
                aria-label="播放进度"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(mobileProgress)}
                tabIndex={0}
                className="group absolute bottom-2 left-0 right-0 z-30 flex h-6 cursor-pointer touch-none items-center px-1"
                onClick={(event) => event.stopPropagation()}
                onTouchStart={(event) => event.stopPropagation()}
                onTouchEnd={(event) => event.stopPropagation()}
                onPointerDown={handleMobileProgressDown}
                onPointerMove={handleMobileProgressMove}
                onPointerUp={finishMobileSeek}
                onPointerCancel={finishMobileSeek}
              >
                <div className="relative h-1 w-full rounded-full bg-background-700/80">
                  <div className="h-full rounded-full bg-accent-500" style={{ width: `${mobileProgress}%` }} />
                  <span
                    className="absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full border border-foreground-50/80 bg-accent-500 shadow-[0_0_10px_rgba(63,218,160,0.55)]"
                    style={{ left: `calc(${mobileProgress}% - 7px)` }}
                  />
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ====== DESKTOP: full-screen video with right-side nav arrows ====== */}
      <div
        className={`${desktopViewport ? 'block' : 'hidden'} relative w-full h-[calc(100vh-64px)] bg-background-950 overflow-hidden`}
        onWheel={handleDesktopWheel}
        onMouseEnter={() => setDesktopHovering(true)}
        onMouseMove={() => setDesktopHovering(true)}
        onMouseLeave={() => setDesktopHovering(false)}
      >
        {/* Video background */}
        <div
          className="absolute inset-0 bg-black transition-opacity duration-300"
        >
          {slideTransition && (
            <div
              key={`desktop-outgoing-${slideTransition.sequence}`}
              className={`pointer-events-none absolute inset-0 z-[5] bg-black ${slideTransition.direction === 'next' ? 'cinema-slide-exit-up' : 'cinema-slide-exit-down'}`}
            >
              <img src={slideTransition.previousVideo.coverUrl} alt="" className="h-full w-full object-contain" />
            </div>
          )}
          {currentVideo && (
            <div
              key={currentVideo.id}
              className={`relative z-10 h-full w-full will-change-transform ${slideTransition ? (slideTransition.direction === 'next' ? 'cinema-slide-enter-up' : 'cinema-slide-enter-down') : ''}`}
            >
              <video
                ref={desktopVideoRef}
                data-cinema-main-video="true"
                poster={currentVideo.coverUrl}
                className="absolute inset-0 w-full h-full object-contain bg-black"
                autoPlay
                muted={desktopViewport ? muted : true}
                playsInline
                preload="auto"
                onEnded={handlePlaybackEnded}
                onClick={toggleDesktopPlay}
                onCanPlay={() => {
                  const video = desktopVideoRef.current;
                  if (!video) return;
                  void video.play().then(() => setDesktopPlaying(true)).catch(() => setDesktopPlaying(false));
                }}
              />
              {subtitleText && (
                <div className="pointer-events-none absolute inset-x-8 bottom-8 z-40 flex justify-center">
                  <p className="max-w-[72%] whitespace-nowrap rounded-md bg-black/65 px-4 py-1.5 text-center text-base font-medium leading-relaxed text-white shadow-xl">
                    {subtitleText}
                  </p>
                </div>
              )}
              {/* Bottom gradient for text readability */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-background-950/30 via-transparent to-background-950/30" />

              {/* Video info overlay */}
              <div className={`absolute bottom-8 left-8 z-20 max-w-lg transition-opacity duration-300 ${desktopHovering || !desktopPlaying ? 'opacity-100' : 'opacity-80'}`}>
                <div className="flex items-center gap-2">
                  <h3 className="min-w-0 truncate text-xl font-bold text-foreground-50">{currentVideo.title}</h3>
                  {displayTotalEpisodes > 0 && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openEpisodeDialog();
                      }}
                      className="shrink-0 rounded-lg border border-[#F97316] bg-background-950/70 px-2.5 py-1 text-sm font-semibold text-[#F97316] backdrop-blur-sm transition-colors hover:bg-[#F97316] hover:text-background-950"
                      title={t('cinema_episodeList')}
                      aria-label={t('cinema_episodeList')}
                    >
                      {t('cinema_unlocked')} {displayUnlockedCount}/{displayTotalEpisodes}
                    </button>
                  )}
                </div>
              </div>

              {!desktopPlaying && (
                <button
                  type="button"
                  onClick={toggleDesktopPlay}
                  className="absolute inset-0 z-10 flex items-center justify-center bg-background-950/20"
                >
                  <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-950/60 text-accent-500 backdrop-blur-sm">
                    <i className="ri-play-fill text-3xl"></i>
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right side: nav arrows */}
        <div className={`absolute right-4 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-3 transition-opacity duration-300 ${desktopHovering || !desktopPlaying ? 'opacity-100' : 'opacity-70'}`}>
          {/* Mute toggle */}
          <div className="relative">
            <button
              onClick={handleManualMuteToggle}
              className={`w-10 h-10 flex items-center justify-center rounded-full bg-background-950/60 backdrop-blur-sm text-foreground-200 hover:bg-background-950/80 transition-colors cursor-pointer ${soundGuideOpen && muted ? 'ring-2 ring-accent-400 ring-offset-2 ring-offset-black/70 animate-pulse' : ''}`}
              title={muted ? 'Unmute' : 'Mute'}
            >
              <i className={muted ? 'ri-volume-mute-line text-lg' : 'ri-volume-up-line text-lg'}></i>
            </button>
            {soundGuideOpen && muted && (
              <div className="absolute right-14 top-0 z-40 w-48 animate-pulse rounded-lg border border-accent-400/60 bg-background-900 px-3 py-3 text-left shadow-xl shadow-black/40">
                <span className="absolute -right-1.5 top-3 h-3 w-3 rotate-45 border-r border-t border-accent-400/60 bg-background-900" />
                <button
                  type="button"
                  onClick={dismissSoundGuide}
                  className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center text-foreground-500 hover:text-foreground-100"
                  aria-label={t('cinema_soundGuideDismiss')}
                >
                  <i className="ri-close-line text-sm" />
                </button>
                <p className="pr-4 text-sm font-semibold text-foreground-50">{t('cinema_soundGuideTitle')}</p>
                <button
                  type="button"
                  onClick={enableSoundFromGuide}
                  className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-md bg-accent-500 px-3 py-2 text-xs font-bold text-background-950"
                >
                  <i className="ri-volume-up-line" />
                  {t('cinema_soundGuideAction')}
                </button>
              </div>
            )}
          </div>

          {/* Prev */}
          <button
            onClick={goPrev}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-background-950/60 text-foreground-200 hover:bg-background-950/80 backdrop-blur-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-up-line text-lg"></i>
          </button>

          {/* Next */}
          <button
            onClick={goNext}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-background-950/60 text-foreground-200 hover:bg-background-950/80 backdrop-blur-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-down-line text-lg"></i>
          </button>
        </div>

        {/* Bottom action bar: like + comment only */}
        <div className={`absolute bottom-8 right-4 z-20 flex flex-col items-center gap-2 transition-opacity duration-300 ${desktopHovering || !desktopPlaying ? 'opacity-100' : 'opacity-70'}`}>
          {currentVideo && (
            <>
              <button
                onClick={() => toggleLike(currentVideo.id)}
                className="flex flex-col items-center gap-0.5 cursor-pointer group"
              >
                <span className={`w-10 h-10 flex items-center justify-center rounded-full bg-background-950/60 backdrop-blur-sm text-xl transition-colors ${likedSet.has(currentVideo.id) ? 'text-red-400' : 'text-foreground-100 group-hover:text-red-400'}`}>
                  <i className={likedSet.has(currentVideo.id) ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                </span>
                <span className="text-[10px] text-foreground-300 font-medium">
                  {formatCount(currentVideo.likes + (likedSet.has(currentVideo.id) ? 1 : 0))}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setShareRewardOpen(true)}
                className="flex flex-col items-center gap-0.5 cursor-pointer group"
                title={t('cinema_share')}
              >
                <span className="flex h-10 w-10 items-center justify-center [perspective:150px]">
                  <img
                    src="/images/reward-coin.webp"
                    alt=""
                    aria-hidden="true"
                    className="animate-coin-spin-y-forward h-9 w-9 object-contain drop-shadow-[0_3px_7px_rgba(245,184,65,0.58)]"
                  />
                </span>
                <span className="text-[10px] text-foreground-300 font-medium">{t('cinema_share')}</span>
              </button>

              {renderSettingsControl()}

            </>
          )}
        </div>

        <div
          ref={desktopProgressRef}
          role="slider"
          aria-label="播放进度"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(desktopProgress)}
          tabIndex={0}
          className="group absolute bottom-0 left-0 right-0 z-30 flex h-5 cursor-pointer touch-none items-end"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={handleDesktopProgressDown}
          onPointerMove={handleDesktopProgressMove}
          onPointerUp={finishDesktopSeek}
          onPointerCancel={finishDesktopSeek}
        >
          <div className="relative h-1 w-full bg-background-800/80 transition-[height] group-hover:h-2">
            <div className="h-full bg-accent-500" style={{ width: `${desktopProgress}%` }} />
            <div
              className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border border-foreground-50/70 bg-accent-500 opacity-0 shadow-[0_0_12px_rgba(63,218,160,0.55)] transition-opacity group-hover:opacity-100"
              style={{ left: `calc(${desktopProgress}% - 6px)` }}
            />
          </div>
        </div>

        {/* Top-left: counter */}
        <div className="absolute top-4 left-4 z-20">
          <span className="px-3 py-1 rounded-full bg-background-950/60 backdrop-blur-sm text-xs text-foreground-300 font-medium">
            {activeIdx + 1} / {videos.length}
          </span>
        </div>
      </div>

      {episodeDialogOpen && currentVideo && (
        <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-center justify-center px-4 pt-4 sm:p-4">
          <div className="absolute inset-0 bg-black/72 backdrop-blur-[2px]" onClick={() => setEpisodeDialogOpen(false)} />
          <div className="relative w-full max-w-[340px] rounded-[24px] border border-background-700 bg-[#171222] p-5 shadow-[0_24px_64px_rgba(0,0,0,0.42)]">
            <button
              type="button"
              onClick={() => setEpisodeDialogOpen(false)}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-foreground-50/10 hover:text-foreground-100"
              aria-label={t('cinema_cancel')}
            >
              <i className="ri-close-line text-lg" />
            </button>
            {activeDetailMatchesCurrent ? <>
              <div className="pr-8 text-center">
                <h3 className="text-base font-bold text-foreground-50">{currentVideo.title}</h3>
                <p className="mt-2 text-sm font-medium text-accent-400">{t('cinema_unlocked')} {activeDetail!.unlockedCount}/{activeDetail!.totalEpisodes}</p>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-2.5">
              {activeDetail!.episodes.map((episode) => {
                const isUnlocked = episode.isUnlocked;
                const isPlaying = String(episode.id) === playingEpisodeId;
                const isNextLocked = String(episode.id) === String(unlockEpisode?.id || '');
                return (
                  <button
                    key={episode.id}
                    type="button"
                    disabled={!isUnlocked && !isNextLocked}
                    onClick={() => {
                      if (isUnlocked) {
                        playUnlockedEpisode(episode);
                        return;
                      }
                      setEpisodeDialogOpen(false);
                      requestUnlock();
                    }}
                    className={`relative flex h-[62px] flex-col items-center justify-center rounded-[16px] border px-2 text-center transition-colors ${
                      isPlaying
                        ? 'border-accent-500 bg-[#211b32] text-foreground-50'
                        : isUnlocked
                          ? 'border-foreground-50/80 bg-[#211b32] text-foreground-50 hover:border-accent-500'
                          : isNextLocked
                            ? 'border-foreground-50/15 bg-[#15111f] text-foreground-400 hover:border-accent-500'
                            : 'cursor-not-allowed border-transparent bg-[#15111f] text-foreground-600/50'
                    }`}
                  >
                    <span className="text-sm font-bold">{t('cinema_episodeNumber', { number: episode.episodeNo })}</span>
                    <span className={`mt-1 text-[11px] ${isPlaying ? 'text-accent-400' : isUnlocked ? 'text-foreground-400' : 'text-foreground-500'}`}>
                      {isPlaying ? t('cinema_playing') : isUnlocked ? t('cinema_unlocked') : t('cinema_locked')}
                    </span>
                    {!isUnlocked && <i className="ri-lock-line absolute right-2 top-2 text-xs text-foreground-400" />}
                  </button>
                );
              })}
              </div>
            </> : (
              <div className="flex h-36 items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-2xl text-foreground-500" />
              </div>
            )}
          </div>
        </div>
      )}

      {unlockDialogOpen && currentVideo && unlockEpisode && (
        <div className="cinema-modal-safe fixed inset-0 z-[60] flex items-center justify-center px-4 pt-4 sm:p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setUnlockDialogOpen(false)} />
          <div className="relative w-full max-w-[360px] rounded-2xl border border-background-800 bg-background-900 p-4 shadow-2xl sm:p-5">
            <button
              type="button"
              onClick={() => setUnlockDialogOpen(false)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background-800 text-foreground-500 hover:text-foreground-300"
            >
              <i className="ri-close-line" />
            </button>
            <div className="text-center">
              <span className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-accent-500/15 text-accent-500 sm:h-12 sm:w-12">
                <i className="ri-lock-line text-xl" />
              </span>
              <h3 className="text-base font-bold text-foreground-50">{t('cinema_unlock')}</h3>
              <p className="mt-1 text-xs text-foreground-400 sm:text-sm">「{currentVideo.title}」第{unlockEpisode.episodeNo}集</p>
              <p className="mt-2 text-xs text-foreground-500">{t('cinema_unlockDesc', { cost: unlockEpisode.unlockPoints })}</p>
              <p className="mt-2 text-xs text-foreground-500">{t('cinema_balance', { coins: member.pointsBalance })}</p>
              {showInsufficient && <p className="mt-3 text-xs text-red-400">{t('cinema_insufficient')}</p>}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2.5">
              <button type="button" onClick={() => setUnlockDialogOpen(false)} className="rounded-xl bg-background-800 py-2.5 text-sm font-medium text-foreground-400 hover:bg-background-700">
                {t('cinema_cancel')}
              </button>
              <button type="button" onClick={confirmUnlock} disabled={unlocking} className="rounded-xl bg-accent-500 py-2.5 text-sm font-semibold text-background-950 hover:bg-accent-500/90 disabled:opacity-50">
                {unlocking ? t('cinema_loading') : t('cinema_unlockConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {playbackEndedDialogOpen && currentVideo && (
        <div className="cinema-modal-safe fixed inset-0 z-[65] flex items-center justify-center px-5 pt-4 sm:p-4">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => setPlaybackEndedDialogOpen(false)} />
          <div className="relative w-full max-w-[330px] rounded-2xl border border-background-700 bg-background-900 p-5 text-center shadow-2xl">
            <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
              <i className="ri-movie-2-line text-xl" aria-hidden="true" />
            </span>
            <h3 className="mt-3 text-base font-bold text-foreground-50">{t('cinema_episodeFinished')}</h3>
            <p className="mt-1 text-xs text-foreground-500">{currentVideo.title}</p>
            <div className="mt-5 grid grid-cols-2 gap-2.5">
              <button
                type="button"
                disabled={!unlockEpisode}
                onClick={() => {
                  setPlaybackEndedDialogOpen(false);
                  requestUnlock();
                }}
                className="rounded-xl bg-accent-500 px-2 py-3 text-sm font-bold text-background-950 transition-colors hover:bg-accent-500/90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {unlockEpisode ? t('cinema_unlockNextEpisode') : t('cinema_allUnlocked')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPlaybackEndedDialogOpen(false);
                  goNext();
                }}
                className="rounded-xl bg-background-800 px-2 py-3 text-sm font-semibold text-foreground-200 transition-colors hover:bg-background-700"
              >
                {t('cinema_changeDrama')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ShareRewardDialog open={shareRewardOpen} onClose={() => setShareRewardOpen(false)} />

      <CinemaRechargeModal
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        loadOptions={async () => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return fetchMemberPaymentOptions(token);
        }}
        createTicket={async (packageId, payMethod) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return createMemberPaymentTicket(packageId, payMethod, token);
        }}
        createOrder={async (packageId, payMethod) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return createMemberRechargeOrder(packageId, payMethod, token);
        }}
        queryOrder={async (outTradeNo) => {
          const token = await ensureMemberAccessToken();
          if (!token) throw new Error(t('cinema_login'));
          return queryMemberRechargeOrder(outTradeNo, token);
        }}
        onRechargeSuccess={() => {
          void refreshCurrentMember().then(setMember);
        }}
      />
    </div>
  );
}
